/**
 * 푸시 알림 가용성 판정 (순수 함수 — DOM 조작도 부수효과도 없다).
 *
 * 이 파일이 생기기 전에는 iOS 판정이 PwaInstallBanner 안에 인라인으로만 있었고
 * iOS 16.4 버전 판정은 아예 없었다. "왜 알림이 안 오는지"를 화면에 설명하려면
 * 판정이 한 곳에 모여 있어야 한다.
 */

import { isTauri } from './tauri';

/** iPadOS 13+는 데스크톱 UA를 쓰므로 maxTouchPoints로 갈라낸다. */
export const isIOS = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/** 홈 화면에서 실행 중인가. iOS는 표준 display-mode 대신 navigator.standalone을 쓴다. */
export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as any).standalone === true;

/**
 * "OS 16_4 like Mac OS X" → 16.4.
 * iPadOS 데스크톱 모드에는 이 토큰이 없어 null을 반환한다 — 그때는 버전 판정을 포기한다.
 */
export const getIOSVersion = (): number | null => {
  const m = /OS (\d+)[._](\d+)/.exec(navigator.userAgent);
  return m ? Number(`${m[1]}.${m[2]}`) : null;
};

export type PushCapability =
  | 'ok'                 // 권한만 받으면 푸시가 동작한다
  | 'ios-needs-install'  // iOS Safari 탭 — 홈 화면에 추가해야 알림 API가 열린다
  | 'ios-too-old'        // iOS 16.4 미만 — 웹푸시 자체가 없다
  | 'unsupported';       // 그 밖에 API가 없는 환경

/**
 * ⚠ 순서가 핵심이다. **기능 프로브를 먼저 보고, UA는 "왜 없는지"를 설명할 때만 쓴다.**
 * UA 문자열은 거짓말을 하지만 `'PushManager' in window`는 하지 않는다 —
 * iOS Safari는 홈 화면에 추가된 상태에서만 PushManager를 노출하므로 프로브가 곧 판정이다.
 */
export const getPushCapability = (): PushCapability => {
  if (isTauri()) return 'ok';
  if ('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window) return 'ok';
  if (isIOS()) {
    const v = getIOSVersion();
    if (v !== null && v < 16.4) return 'ios-too-old';
    // 버전을 못 읽으면(iPadOS 데스크톱 모드) 설치 안내 쪽으로 보낸다 — 조치 가능한 안내가 낫다.
    return isStandalone() ? 'unsupported' : 'ios-needs-install';
  }
  return 'unsupported';
};

/** Notification API가 없는 환경에서도 안전하게 현재 권한을 읽는다. */
export const readPermission = (): NotificationPermission | 'unsupported' =>
  'Notification' in window ? Notification.permission : 'unsupported';

export type NotificationGate = 'install' | 'permission' | 'none';

/**
 * 어느 배너를 띄울지 정한다. 두 배너가 각자 판단하던 것을 한 곳으로 모은 이유:
 * 예전에는 둘이 무조건 형제로 렌더돼 동시에 뜰 수 있었고, 각자 7일 쿨다운을 가져
 * 둘 다 닫으면 일주일간 아무 안내도 남지 않았다.
 *
 * ⚠ 순수 함수로 유지할 것 — 분기를 잘못 짜면 배너가 전멸해 신규 사용자가 알림을 켤 수 없다.
 */
export const pickNotificationBanner = (
  capability: PushCapability = getPushCapability(),
  permission: NotificationPermission | 'unsupported' = readPermission(),
  appEnabled: boolean = true,
): NotificationGate => {
  if (appEnabled === false) return 'none';                   // 사용자가 이 기기 알림을 껐다 — 조르지 않는다
  if (capability === 'ios-needs-install') return 'install';  // 설치 전엔 권한 요청 자체가 불가능
  if (capability !== 'ok') return 'none';                    // too-old/unsupported는 조를 게 없다
  if (permission === 'default') return 'permission';         // 권한이 최우선
  if (permission === 'denied') return 'none';                // NotificationSettingsGuide 담당
  return isStandalone() ? 'none' : 'install';                // granted + 미설치 → 설치 권유(최하위)
};

export interface PushStatusInfo {
  /** 설정 목록 행의 trailing 칩 문구 */
  label: string;
  /** 패널에서 현재 상태를 한 줄로 */
  headline: string;
  /** 왜 그런지 + 무엇을 하면 되는지 */
  detail: string;
  /** 사용자가 지금 할 수 있는 일 */
  action: 'request' | 'guide' | 'install' | 'none';
}

/**
 * 알림 상태를 사용자 문구로 옮긴다.
 * 카피 규칙: 오류는 "무엇을 못했는지 + 다음 행동", 상태 설명은 마침표 있는 완결 문장.
 */
export const describePushStatus = (
  capability: PushCapability,
  permission: NotificationPermission | 'unsupported',
  /** 앱 안 "이 기기에서 알림 받기" 토글 (utils/webNotifyPref) — 브라우저 denied와 다른 상태다 */
  appEnabled: boolean = true,
): PushStatusInfo => {
  if (capability === 'ios-needs-install') {
    return {
      label: '홈 화면 추가 필요',
      headline: '아직 알림을 받을 수 없습니다',
      detail: '아이폰은 홈 화면에 추가한 뒤 실행해야 알림을 받을 수 있습니다. 사파리 아래쪽 공유 버튼을 누르고 "홈 화면에 추가"를 선택해 주세요.',
      action: 'install',
    };
  }
  if (capability === 'ios-too-old') {
    return {
      label: 'iOS 16.4 이상 필요',
      headline: '이 기기에서는 알림을 받을 수 없습니다',
      detail: '웹 알림은 iOS 16.4부터 지원됩니다. iOS를 업데이트하거나 PC 앱을 사용해 주세요.',
      action: 'none',
    };
  }
  if (capability === 'unsupported' || permission === 'unsupported') {
    return {
      label: '지원 안 함',
      headline: '이 브라우저는 알림을 지원하지 않습니다',
      detail: '다른 브라우저에서 열거나 PC 앱을 사용해 주세요.',
      action: 'none',
    };
  }
  // 앱 안 토글로 끈 상태 — 브라우저 차단(denied)과 구분한다 (앱에서 스스로 되돌릴 수 있다)
  if (appEnabled === false) {
    return {
      label: '이 기기에서 끔',
      headline: '이 기기에서는 알림을 받지 않습니다',
      detail: '다른 기기의 알림은 그대로 유지됩니다. 아래에서 다시 켤 수 있습니다.',
      action: 'none',
    };
  }
  if (permission === 'granted') {
    return {
      label: '켜짐',
      headline: '알림을 받고 있습니다',
      detail: '앱을 닫아도 새 메시지와 전화 알림이 옵니다. 대화방별로 끄려면 목록에서 그 방을 길게 누르거나 우클릭해 주세요.',
      action: 'none',
    };
  }
  if (permission === 'denied') {
    return {
      label: '차단됨',
      headline: '알림이 차단되어 있습니다',
      detail: '브라우저에서 차단한 알림은 앱에서 다시 켤 수 없습니다. 아래 안내대로 브라우저 설정에서 허용해 주세요.',
      action: 'guide',
    };
  }
  return {
    label: '꺼짐',
    headline: '알림이 꺼져 있습니다',
    detail: '앱을 사용하지 않을 때도 새 메시지 알림을 받으려면 알림을 허용해 주세요.',
    action: 'request',
  };
};
